// Protocols are workflows of type 'protocol', so this calls the workflow endpoints.

import { workflowService, type WorkflowCategoryTree } from './workflow.service';
import { ProtocolAdapter } from '@/adapters';
import { PROTOCOL_WORKFLOW_TYPE } from '@/modules/protocols/constants';
import type { PatchSEGNodeDto, VectorSyncResult } from '@/types';
import type { CreateProtocolInput, Protocol, UpdateProtocolInput } from '@/modules/protocols/types';

export const protocolService = {
  async getProtocolById(id: string): Promise<Protocol> {
    return ProtocolAdapter.fromWorkflow(await workflowService.getWorkflowById(id));
  },

  async createProtocol(data: CreateProtocolInput, scope: { bu_id?: string } = {}): Promise<Protocol> {
    const workflow = await workflowService.createWorkflow(
      {
        name: data.name,
        type: PROTOCOL_WORKFLOW_TYPE,
        description: data.description,
        editorData: ProtocolAdapter.toEditorData(data.protocol)
      },
      scope
    );
    return ProtocolAdapter.fromWorkflow(workflow);
  },

  // A save marks the graph stale; `compile` also starts the guided compile on the server.
  async updateProtocol(id: string, data: UpdateProtocolInput, { compile = false } = {}): Promise<Protocol> {
    const workflow = await workflowService.updateWorkflow(id, {
      name: data.name,
      description: data.description,
      editorData: ProtocolAdapter.toEditorData(data.protocol),
      compilationStatus: 'pending',
      ...(compile && { compileGuided: true }),
      ...(data.baseEditorDataUpdatedAt !== undefined && { baseEditorDataUpdatedAt: data.baseEditorDataUpdatedAt })
    });
    return ProtocolAdapter.fromWorkflow(workflow);
  },

  async deleteProtocol(id: string): Promise<void> {
    await workflowService.deleteWorkflow(id);
  },

  // Set Live / take offline: the workflow status toggle.
  async toggleProtocolStatus(id: string): Promise<Protocol> {
    return ProtocolAdapter.fromWorkflow(await workflowService.toggleWorkflowStatus(id));
  },

  async updateProtocolChannels(id: string, channels: string[]): Promise<Protocol> {
    return ProtocolAdapter.fromWorkflow(await workflowService.updateWorkflowChannels(id, channels));
  },

  // One node of the compiled graph — pre-approval, or a hand-edited body.
  async patchSegNode(id: string, body: PatchSEGNodeDto): Promise<Protocol> {
    return ProtocolAdapter.fromWorkflow(await workflowService.patchSEGNode(id, body));
  },

  // Push this protocol's SEG nodes to Pinecone again.
  async resyncProtocol(id: string): Promise<VectorSyncResult> {
    return workflowService.resyncWorkflow(id);
  },

  // Filing only — the backend keeps a live protocol live for this.
  async updateProtocolCategory(id: string, category: string, subcategory: string): Promise<Protocol> {
    return ProtocolAdapter.fromWorkflow(await workflowService.updateWorkflow(id, { category, subcategory }));
  },

  async getCategoryTree(scope: { bu_id?: string } = {}): Promise<WorkflowCategoryTree> {
    return workflowService.getCategoryTree(undefined, scope);
  },

  async autoCategorize(scope: { bu_id?: string } = {}): Promise<{ scanned: number; categorized: number }> {
    return workflowService.autoCategorize(scope);
  },

  async generateFromPrompt(prompt: string, scope: { bu_id?: string } = {}): Promise<Protocol> {
    return ProtocolAdapter.fromWorkflow(await workflowService.generateProtocol(prompt, scope));
  },

  // The protocol's text format — what its SOP holds and search/backtests read.
  async getProtocolText(id: string): Promise<string> {
    return workflowService.getWorkflowText(id);
  }
};
